import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import fetchAPI from '../../../lib/utils/fetch-api'
import { ImportUsersBlock } from '../../components/v2/import-users-block'
import type { OfficeSelectOption } from '../../lib/import-upload'

/**
 * ПРОТОТИП единого раздела «Сотрудники» (/personnel).
 * Старые страницы (офисы, пользователи, роли, права) не тронуты.
 */

type Employee = {
  id: number
  name: string | null
  login: string | null
  phone: string | null
  is_active: boolean
  is_tenant_owner: boolean
  office_id: number | null
  organization_id: number | null
  role_ids: number[]
}
type Role = { id: number; name: string; is_active: boolean; users_count: number }
type Office = {
  id: number
  name: string
  address: string | null
  is_active: boolean
  organization_id: number | null
  organization_name: string | null
  employees_count: number
}
type Overview = { employees: Employee[]; roles: Role[]; offices: Office[] }

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Российский номер → 7XXXXXXXXXX или null (как на бэкенде). */
export function normalizeRuPhone(raw: unknown): string | null {
  let digits = String(raw ?? '').replace(/\D/g, '')
  if (digits.length === 10) digits = `7${digits}`
  else if (digits.length === 11 && digits.startsWith('8')) digits = `7${digits.slice(1)}`
  return /^7\d{10}$/.test(digits) ? digits : null
}

export function formatRuPhone(raw: string | null | undefined): string {
  const p = normalizeRuPhone(raw)
  if (!p) return raw || ''
  return `+7 ${p.slice(1, 4)} ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9, 11)}`
}

function generatePassword(length = 10): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
  const values = new Uint32Array(length)
  window.crypto.getRandomValues(values)
  return Array.from(values, v => chars[v % chars.length]).join('')
}

const phoneRule = {
  validator: (_: unknown, value: string) =>
    !value || normalizeRuPhone(value)
      ? Promise.resolve()
      : Promise.reject(new Error('Российский номер, например +7 900 123-45-67')),
}
const loginRule = {
  validator: (_: unknown, value: string) =>
    !value || !value.includes('@') || EMAIL_RE.test(value.trim())
      ? Promise.resolve()
      : Promise.reject(new Error('Введите корректный email')),
}

function apiError(res: any, fallback: string): string | null {
  if (!res) return fallback
  if (Array.isArray(res.errors) && res.errors.length) return res.errors[0]?.detail || fallback
  if (res.error) return typeof res.error === 'string' ? res.error : res.error.message || fallback
  return null
}

async function call(path: string, body?: unknown): Promise<any> {
  try {
    return await fetchAPI(
      path,
      body === undefined ? {} : { method: 'POST', body: JSON.stringify(body) },
      true,
    )
  } catch (e: any) {
    return { errors: [{ detail: e?.message || 'Ошибка запроса' }] }
  }
}

/** Сервер: формат/уникальность email и телефон. Ошибку поля показываем в форме. */
function applyFieldError(form: any, res: any, fallback: string): string {
  const field = res?.errors?.[0]?.field
  const detail = apiError(res, fallback) || fallback
  if (field) form.setFields([{ name: field, errors: [detail] }])
  return detail
}

const PersonnelPage: React.FC = () => {
  const [data, setData] = useState<Overview>({ employees: [], roles: [], offices: [] })
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [tab, setTab] = useState('employees')
  const [search, setSearch] = useState('')
  const [officeFilter, setOfficeFilter] = useState<number | undefined>()
  const [roleFilter, setRoleFilter] = useState<number | undefined>()
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | undefined>()
  const [editing, setEditing] = useState<Employee | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const res = await call('/api/personnel/overview')
    const err = apiError(res, 'Не удалось загрузить данные')
    setLoadError(res?.data ? null : err)
    if (res?.data) setData(res.data)
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const officeById = useMemo(() => new Map(data.offices.map(o => [o.id, o])), [data.offices])
  const roleById = useMemo(() => new Map(data.roles.map(r => [r.id, r])), [data.roles])
  const officeSelect = useMemo(
    () =>
      data.offices.map(o => ({
        value: o.id,
        label: o.organization_name ? `${o.name} (${o.organization_name})` : o.name,
      })),
    [data.offices],
  )
  const roleSelect = useMemo(() => data.roles.map(r => ({ value: r.id, label: r.name })), [data.roles])
  const importOffices: OfficeSelectOption[] = useMemo(
    () =>
      data.offices
        .filter(o => o.organization_id)
        .map(o => ({
          officeId: o.id,
          organizationId: o.organization_id as number,
          label: o.organization_name ? `${o.name} (${o.organization_name})` : o.name,
        })),
    [data.offices],
  )

  const employees = useMemo(() => {
    const q = search.trim().toLowerCase()
    const qDigits = q.replace(/\D/g, '')
    return data.employees.filter(e => {
      if (officeFilter && e.office_id !== officeFilter) return false
      if (roleFilter && !e.role_ids.includes(roleFilter)) return false
      if (statusFilter === 'active' && !e.is_active) return false
      if (statusFilter === 'inactive' && e.is_active) return false
      if (!q) return true
      return (
        (e.name || '').toLowerCase().includes(q) ||
        (e.login || '').toLowerCase().includes(q) ||
        (qDigits.length >= 3 && (e.phone || '').replace(/\D/g, '').includes(qDigits))
      )
    })
  }, [data.employees, search, officeFilter, roleFilter, statusFilter])

  const columns: ColumnsType<Employee> = [
    {
      title: 'ФИО',
      dataIndex: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (v, e) => (
        <Space>
          <a onClick={() => setEditing(e)}>{v || '—'}</a>
          {e.is_tenant_owner && <Tag color="gold">Владелец</Tag>}
        </Space>
      ),
    },
    { title: 'Телефон', dataIndex: 'phone', render: v => formatRuPhone(v) || '—' },
    { title: 'Email / логин', dataIndex: 'login', render: v => v || '—' },
    {
      title: 'Офис',
      dataIndex: 'office_id',
      render: v => (v ? officeById.get(v)?.name || `#${v}` : '—'),
    },
    {
      title: 'Роли',
      dataIndex: 'role_ids',
      render: (ids: number[]) =>
        ids.length ? ids.map(id => <Tag key={id}>{roleById.get(id)?.name || `#${id}`}</Tag>) : '—',
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      render: v => (v ? <Tag color="green">Активен</Tag> : <Tag>Отключён</Tag>),
    },
  ]

  const employeesTab = (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Space wrap>
        <Input.Search
          allowClear
          placeholder="ФИО, телефон или email"
          style={{ width: 280 }}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Офис"
          style={{ width: 220 }}
          options={officeSelect}
          value={officeFilter}
          onChange={setOfficeFilter}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Роль"
          style={{ width: 200 }}
          options={roleSelect}
          value={roleFilter}
          onChange={setRoleFilter}
        />
        <Select
          allowClear
          placeholder="Статус"
          style={{ width: 150 }}
          options={[
            { value: 'active', label: 'Активные' },
            { value: 'inactive', label: 'Отключённые' },
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </Space>
      <Table
        rowKey="id"
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={employees}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        onRow={e => ({ onDoubleClick: () => setEditing(e) })}
      />
    </Space>
  )

  const rolesTab = (
    <Table
      rowKey="id"
      size="middle"
      loading={loading}
      dataSource={data.roles}
      pagination={false}
      columns={[
        { title: 'Роль', dataIndex: 'name' },
        { title: 'Сотрудников', dataIndex: 'users_count', width: 140 },
        {
          title: 'Статус',
          dataIndex: 'is_active',
          width: 120,
          render: v => (v ? <Tag color="green">Активна</Tag> : <Tag>Отключена</Tag>),
        },
        {
          title: '',
          width: 220,
          render: (_, r: Role) => (
            <Space>
              <Link to={`/admin/role/${r.id}`}>Права роли</Link>
              <a
                onClick={() => {
                  setRoleFilter(r.id)
                  setTab('employees')
                }}
              >
                Сотрудники
              </a>
            </Space>
          ),
        },
      ]}
    />
  )

  const officesTab = (
    <Table
      rowKey="id"
      size="middle"
      loading={loading}
      dataSource={data.offices}
      pagination={false}
      columns={[
        { title: 'Офис', dataIndex: 'name', render: (v, o: Office) => <Link to={`/office/${o.id}`}>{v}</Link> },
        { title: 'Организация', dataIndex: 'organization_name', render: v => v || '—' },
        { title: 'Адрес', dataIndex: 'address', render: v => v || '—' },
        { title: 'Сотрудников', dataIndex: 'employees_count', width: 140 },
        {
          title: '',
          width: 140,
          render: (_, o: Office) => (
            <a
              onClick={() => {
                setOfficeFilter(o.id)
                setTab('employees')
              }}
            >
              Сотрудники
            </a>
          ),
        },
      ]}
    />
  )

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Сотрудники
        </Typography.Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} />
          <Button onClick={() => setImportOpen(true)}>Импорт из Excel</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            Добавить сотрудника
          </Button>
        </Space>
      </Space>
      {loadError && <Alert type="error" showIcon message={loadError} style={{ marginBottom: 16 }} />}
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: 'employees', label: `Сотрудники (${data.employees.length})`, children: employeesTab },
          { key: 'roles', label: 'Роли и права', children: rolesTab },
          { key: 'offices', label: 'Офисы', children: officesTab },
        ]}
      />

      <EmployeeDrawer
        employee={editing}
        officeSelect={officeSelect}
        roleSelect={roleSelect}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          load()
        }}
      />
      <AddEmployeeModal
        open={addOpen}
        offices={data.offices}
        officeSelect={officeSelect}
        roleSelect={roleSelect}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          setAddOpen(false)
          load()
        }}
      />
      <Modal
        open={importOpen}
        title="Импорт сотрудников из Excel"
        footer={null}
        width={720}
        destroyOnClose
        onCancel={() => setImportOpen(false)}
      >
        <ImportUsersBlock
          officeOptions={importOffices}
          onImported={() => {
            setImportOpen(false)
            load()
          }}
        />
      </Modal>
    </div>
  )
}

type SelectOpts = { value: number; label: string }[]

const EmployeeDrawer: React.FC<{
  employee: Employee | null
  officeSelect: SelectOpts
  roleSelect: SelectOpts
  onClose: () => void
  onSaved: () => void
}> = ({ employee, officeSelect, roleSelect, onClose, onSaved }) => {
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!employee) return
    form.resetFields()
    form.setFieldsValue({
      name: employee.name,
      login: employee.login,
      phone: formatRuPhone(employee.phone),
      office_id: employee.office_id ?? undefined,
      role_ids: employee.role_ids,
      is_active: employee.is_active,
      password: '',
    })
  }, [employee, form])

  const save = async () => {
    if (!employee) return
    const values = await form.validateFields()
    setSaving(true)
    try {
      const res = await call(`/api/users_new/${employee.id}`, {
        name: values.name,
        login: values.login.trim(),
        phone: values.phone ? normalizeRuPhone(values.phone) : null,
        is_active: values.is_active,
        ...(values.password ? { password: values.password } : {}),
      })
      const err = apiError(res, 'Не удалось сохранить')
      if (err) {
        message.error(applyFieldError(form, res, 'Не удалось сохранить'))
        return
      }
      const res2 = await call(`/api/personnel/employees/${employee.id}/assignment`, {
        office_id: values.office_id ?? null,
        role_ids: values.role_ids || [],
      })
      const err2 = apiError(res2, 'Не удалось сохранить офис и роли')
      if (err2) {
        message.error(err2)
        return
      }
      message.success('Сохранено')
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Drawer
      open={Boolean(employee)}
      width={480}
      title={employee?.name || 'Сотрудник'}
      onClose={onClose}
      extra={
        <Space>
          {employee && <Link to={`/employees_new/${employee.id}`}>Полная карточка</Link>}
          <Button type="primary" loading={saving} onClick={save}>
            Сохранить
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="ФИО" rules={[{ required: true, message: 'Укажите ФИО' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="phone" label="Телефон" rules={[phoneRule]}>
          <Input placeholder="+7 900 123-45-67" />
        </Form.Item>
        <Form.Item
          name="login"
          label="Email / логин"
          extra={
            !employee?.login
              ? 'У сотрудника пока нет входа в систему. Укажите email и пароль, чтобы он мог войти.'
              : undefined
          }
          rules={
            employee?.login
              ? [{ required: true, message: 'Укажите email или логин' }, loginRule]
              : [
                  { required: true, message: 'Укажите email, чтобы сотрудник мог войти' },
                  loginRule,
                ]
          }
        >
          <Input placeholder={employee?.login ? undefined : 'email@example.com'} />
        </Form.Item>
        <Form.Item name="office_id" label="Офис">
          <Select showSearch optionFilterProp="label" options={officeSelect} />
        </Form.Item>
        <Form.Item name="role_ids" label="Роли">
          <Select mode="multiple" optionFilterProp="label" options={roleSelect} />
        </Form.Item>
        <Form.Item name="is_active" label="Активен" valuePropName="checked">
          <Switch disabled={employee?.is_tenant_owner} />
        </Form.Item>
        <Form.Item
          name="password"
          label={employee?.login ? 'Новый пароль' : 'Пароль'}
          extra={
            employee?.login
              ? 'Оставьте пустым, чтобы не менять'
              : 'Обязателен при первом назначении email'
          }
          rules={
            employee?.login
              ? []
              : [{ required: true, message: 'Задайте пароль для входа' }]
          }
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Drawer>
  )
}

const AddEmployeeModal: React.FC<{
  open: boolean
  offices: Office[]
  officeSelect: SelectOpts
  roleSelect: SelectOpts
  onClose: () => void
  onCreated: () => void
}> = ({ open, offices, officeSelect, roleSelect, onClose, onCreated }) => {
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const [created, setCreated] = useState<{ login: string; password: string } | null>(null)

  useEffect(() => {
    if (open) {
      form.resetFields()
      form.setFieldsValue({ password: generatePassword() })
      setCreated(null)
    }
  }, [open, form])

  const checkLogin = async () => {
    const login = String(form.getFieldValue('login') || '').trim()
    if (!login) return
    const res = await call('/api/personnel/check-login', { login })
    if (res?.data && res.data.available === false) {
      form.setFields([{ name: 'login', errors: [res.data.detail] }])
    }
  }

  const submit = async () => {
    const values = await form.validateFields()
    const office = offices.find(o => o.id === values.office_id)
    if (!office?.organization_id) {
      form.setFields([{ name: 'office_id', errors: ['У офиса не указана организация'] }])
      return
    }
    setSaving(true)
    try {
      const res = await call('/api/users_new/add', {
        name: values.name,
        login: values.login.trim(),
        phone: values.phone ? normalizeRuPhone(values.phone) : null,
        password: values.password,
        office_id: office.id,
        organization_id: office.organization_id,
      })
      const err = apiError(res, 'Не удалось создать сотрудника')
      if (err) {
        message.error(applyFieldError(form, res, 'Не удалось создать сотрудника'))
        return
      }
      const createdUser = Array.isArray(res?.user) ? res.user[0] : res?.user
      const newId = Number(createdUser?.id)
      if (newId && values.role_id) {
        const res2 = await call(`/api/personnel/employees/${newId}/assignment`, {
          role_ids: [values.role_id],
        })
        const err2 = apiError(res2, 'Сотрудник создан, но роль не назначена')
        if (err2) message.warning(err2)
      } else if (values.role_id) {
        message.warning('Сотрудник создан, назначьте роль в карточке')
      }
      setCreated({ login: values.login.trim(), password: values.password })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      title="Добавить сотрудника"
      onCancel={created ? onCreated : onClose}
      destroyOnClose
      footer={
        created ? (
          <Button type="primary" onClick={onCreated}>
            Готово
          </Button>
        ) : (
          <Space>
            <Button onClick={onClose}>Отмена</Button>
            <Button type="primary" loading={saving} onClick={submit}>
              Создать
            </Button>
          </Space>
        )
      }
    >
      {created ? (
        <Alert
          type="success"
          showIcon
          message="Сотрудник создан"
          description={
            <Typography.Paragraph copyable={{ text: `${created.login}\n${created.password}` }}>
              Логин: <b>{created.login}</b>
              <br />
              Пароль: <b>{created.password}</b>
            </Typography.Paragraph>
          }
        />
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="ФИО" rules={[{ required: true, message: 'Укажите ФИО' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Телефон" rules={[phoneRule]}>
            <Input placeholder="+7 900 123-45-67" />
          </Form.Item>
          <Form.Item
            name="login"
            label="Email / логин"
            rules={[{ required: true, message: 'Укажите email или логин' }, loginRule]}
          >
            <Input onBlur={checkLogin} />
          </Form.Item>
          <Form.Item name="office_id" label="Офис" rules={[{ required: true, message: 'Выберите офис' }]}>
            <Select showSearch optionFilterProp="label" options={officeSelect} />
          </Form.Item>
          <Form.Item name="role_id" label="Роль">
            <Select allowClear showSearch optionFilterProp="label" options={roleSelect} />
          </Form.Item>
          <Form.Item label="Пароль" required>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name="password"
                noStyle
                rules={[{ required: true, min: 6, message: 'Минимум 6 символов' }]}
              >
                <Input autoComplete="new-password" />
              </Form.Item>
              <Button onClick={() => form.setFieldsValue({ password: generatePassword() })}>
                Сгенерировать
              </Button>
            </Space.Compact>
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}

export default PersonnelPage
